import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, Clock, Wrench, Users, Calendar, ChevronRight, CheckCircle2, AlertCircle, Phone, FileText, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';

const STATUS_CONFIG = {
  open: { label: 'En attente', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  signed: { label: 'Devis accepte', color: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  completed: { label: 'Terminee', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
};

const WorkshopDashboard = ({ auth, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTick(p => p + 1), 10000);
    return () => clearInterval(t);
  }, []);

  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/workshop`);
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading) return <AdminLayout auth={auth} onLogout={onLogout}><div className="p-8 text-center text-slate-400">Chargement...</div></AdminLayout>;
  if (!data) return null;

  const stats = data.work_order_stats;
  const allWos = data.vehicles_at_workshop || [];
  const readyWos = data.vehicles_ready || [];
  const appts = data.today_appointments || [];

  const fmtH = (h) => {
    if (!h) return '—';
    const hrs = Math.floor(h);
    const mins = Math.round((h - hrs) * 60);
    return `${hrs}h${mins > 0 ? String(mins).padStart(2, '0') : ''}`;
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-5 max-w-[1500px] mx-auto" data-testid="workshop-dashboard">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-heading font-bold text-slate-900" data-testid="workshop-dashboard-title">Vue Atelier</h1>
          <Button variant="outline" size="sm" onClick={loadData} className="text-xs">Actualiser</Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="A l'atelier" value={allWos.length} color="blue" icon={Wrench} />
          <StatCard label="En attente" value={stats.open + stats.signed} color="indigo" icon={FileText} />
          <StatCard label="En cours" value={stats.in_progress} color="amber" icon={Clock} />
          <StatCard label="Terminees aujourd'hui" value={readyWos.length} color="emerald" icon={CheckCircle2} />
          <StatCard label="RDV du jour" value={appts.length} color="slate" icon={Calendar} />
        </div>

        {/* MAIN TABLE: All vehicles at workshop */}
        <Card className="rounded-xl overflow-hidden" data-testid="workshop-vehicles-table">
          <div className="px-4 py-3 bg-slate-800 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              <span className="text-sm font-bold">Vehicules a l'atelier</span>
            </div>
            <Badge className="bg-white/20 text-white text-xs">{allWos.length} vehicule(s)</Badge>
          </div>

          {allWos.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <Car className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Aucun vehicule a l'atelier actuellement</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Statut</th>
                    <th className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Vehicule</th>
                    <th className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Client</th>
                    <th className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Intervention</th>
                    <th className="text-center py-2.5 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">RDV</th>
                    <th className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Mecanicien</th>
                    <th className="text-center py-2.5 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Alloue</th>
                    <th className="text-center py-2.5 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Restant</th>
                    <th className="text-center py-2.5 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Reprise</th>
                  </tr>
                </thead>
                <tbody>
                  {allWos.map(wo => {
                    const st = STATUS_CONFIG[wo.status] || STATUS_CONFIG.open;
                    const alloc = wo.allocated_hours || 0;
                    const actual = wo.actual_hours || 0;
                    const remaining = wo.remaining_hours || 0;
                    const isOvertime = alloc > 0 && actual > alloc;
                    const hasMechanic = wo.active_mechanics?.length > 0 || wo.assigned_employee_name;
                    return (
                      <tr key={wo.id}
                        className="border-b border-slate-100 hover:bg-blue-50/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/admin/work-orders/${wo.id}`)}
                        data-testid={`workshop-wo-${wo.id}`}>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                            <Badge className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <span className="font-medium text-slate-900">{wo.vehicle_info || '—'}</span>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-slate-700 truncate max-w-[140px]">{wo.client_name}</span>
                            {wo.client_phone && (
                              <a href={`tel:${wo.client_phone}`} onClick={e => e.stopPropagation()} className="text-slate-400 hover:text-blue-600 shrink-0">
                                <Phone className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-slate-500 truncate max-w-[120px]">{wo.intervention_type || wo.category || '—'}</td>
                        <td className="py-2.5 px-3 text-center">
                          {wo.appointment_time ? (
                            <span className="font-mono text-xs font-bold text-slate-800">{wo.appointment_time}</span>
                          ) : <span className="text-slate-300">—</span>}
                          {wo.appointment_date && wo.appointment_date !== new Date().toISOString().slice(0,10) && (
                            <p className="text-[9px] text-slate-400">{wo.appointment_date}</p>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          {wo.active_mechanics?.length > 0 ? (
                            <div className="flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                              <span className="text-xs font-medium text-emerald-700">
                                {wo.active_mechanics.map(m => m.name.split(' ')[0]).join(', ')}
                              </span>
                            </div>
                          ) : wo.assigned_employee_name ? (
                            <span className="text-xs text-indigo-600">{wo.assigned_employee_name}</span>
                          ) : (
                            <span className="text-xs text-slate-400 italic">Non assigne</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {alloc > 0 ? <span className="font-mono text-slate-700">{fmtH(alloc)}</span> : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {alloc > 0 ? (
                            <span className={`font-mono font-bold ${remaining <= 0 ? 'text-red-600' : remaining < 1 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {remaining <= 0 ? 'Depasse' : fmtH(remaining)}
                            </span>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {wo.pickup_time ? (
                            <span className="font-mono text-xs font-bold text-indigo-700">{wo.pickup_time}</span>
                          ) : <span className="text-slate-300">—</span>}
                          {wo.pickup_date && (
                            <p className="text-[9px] text-slate-400">{wo.pickup_date}</p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Bottom grid: RDV + Completed today */}
        <div className="grid md:grid-cols-2 gap-5">
          {/* Today Appointments */}
          <Card className="p-4 rounded-xl" data-testid="today-appointments">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Calendar className="w-4 h-4" /> RDV du jour</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/agenda')} className="text-xs">Agenda</Button>
            </div>
            {appts.length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center">Aucun RDV aujourd'hui</p>
            ) : (
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {appts.map(a => (
                  <div key={a.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100" onClick={() => a.work_order_id ? navigate(`/admin/work-orders/${a.work_order_id}`) : navigate('/admin/agenda')} data-testid={`appt-${a.id}`}>
                    <span className="text-xs font-bold font-mono text-slate-900 w-10">{a.time}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">{a.client_name}</p>
                      <p className="text-[10px] text-slate-500 truncate">{a.vehicle_info}</p>
                    </div>
                    {a.client_phone && (
                      <a href={`tel:${a.client_phone}`} onClick={e => e.stopPropagation()} className="text-slate-400 hover:text-blue-600"><Phone className="w-3 h-3" /></a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Completed Today */}
          <Card className="p-4 rounded-xl" data-testid="vehicles-ready">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Termines aujourd'hui</h2>
              <Badge className="bg-emerald-100 text-emerald-700 text-xs">{readyWos.length}</Badge>
            </div>
            {readyWos.length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center">Aucun vehicule termine aujourd'hui</p>
            ) : (
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {readyWos.map(wo => (
                  <div key={wo.id} className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100" onClick={() => navigate(`/admin/work-orders/${wo.id}`)}>
                    <Car className="w-4 h-4 text-emerald-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">{wo.vehicle_info}</p>
                      <p className="text-[10px] text-slate-500">{wo.client_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

const StatCard = ({ label, value, color, icon: Icon }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600', indigo: 'bg-indigo-50 text-indigo-600',
    amber: 'bg-amber-50 text-amber-600', emerald: 'bg-emerald-50 text-emerald-600',
    slate: 'bg-slate-100 text-slate-600',
  };
  return (
    <Card className="p-3 rounded-xl">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${colors[color]} mb-1`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <p className="text-xl font-bold text-slate-900">{value}</p>
      <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
    </Card>
  );
};

export default WorkshopDashboard;
